import React, { useEffect, useState } from 'react'

const MODES = {
  compose: { title: 'New Message', submitSend: 'Send', submitDraft: 'Save Draft' },
  reply: { title: 'Reply', submitSend: 'Send Reply', submitDraft: 'Save Draft' },
  forward: { title: 'Forward', submitSend: 'Forward', submitDraft: 'Save Draft' },
}

function htmlToPlain(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function plainToHtml(text) {
  return String(text || '')
    .split('\n')
    .map((line) => `<p>${line ? line.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '<br/>'}</p>`)
    .join('')
}

export default function EmailComposeModal({
  open,
  mode = 'compose',
  initial = {},
  accounts = [],
  sending = false,
  onClose,
  onSend,
  onSaveDraft,
}) {
  const [toEmail, setToEmail] = useState('')
  const [ccEmail, setCcEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [bodyText, setBodyText] = useState('')
  const [accountId, setAccountId] = useState('gmail')
  const [showCc, setShowCc] = useState(false)

  useEffect(() => {
    if (!open) return
    setToEmail(initial.toEmail || '')
    setCcEmail(initial.ccEmail || '')
    setSubject(initial.subject || '')
    setBodyText(htmlToPlain(initial.bodyHtml || ''))
    setAccountId(
      initial.accountId
        || accounts.find((a) => a.mailReady !== false)?.id
        || accounts[0]?.id
        || 'gmail'
    )
    setShowCc(!!initial.ccEmail)
  }, [open, initial, accounts])

  if (!open) return null

  const labels = MODES[mode] || MODES.compose

  const payload = () => ({
    toEmail: toEmail.trim(),
    ccEmail: ccEmail.trim(),
    subject: subject.trim(),
    bodyHtml: plainToHtml(bodyText),
    accountId,
    inReplyToId: initial.inReplyToId || null,
    forwardedFromId: initial.forwardedFromId || null,
    draftId: initial.draftId || null,
  })

  return (
    <div className="admin-email-compose-overlay" onClick={onClose} role="presentation">
      <div className="admin-email-compose-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="admin-email-compose-header">
          <span>{labels.title}</span>
          <button type="button" className="admin-email-compose-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="admin-email-compose-fields">
          <label className="admin-email-compose-row">
            <span>From</span>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id} disabled={acc.mailReady === false}>
                  {acc.email} ({acc.label}){acc.mailReady === false ? ' — set up mail access first' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-email-compose-row">
            <span>To</span>
            <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="recipient@company.com" />
          </label>
          {showCc ? (
            <label className="admin-email-compose-row">
              <span>Cc</span>
              <input type="email" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} placeholder="cc@company.com" />
            </label>
          ) : (
            <button type="button" className="admin-email-compose-cc-toggle" onClick={() => setShowCc(true)}>Add Cc</button>
          )}
          <label className="admin-email-compose-row">
            <span>Subject</span>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
          </label>
          <textarea
            className="admin-email-compose-body"
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={12}
            placeholder="Write your message…"
          />
        </div>
        <div className="admin-email-compose-footer">
          <button type="button" className="admin-btn secondary" disabled={sending} onClick={() => onSaveDraft?.(payload())}>{labels.submitDraft}</button>
          <button type="button" className="admin-btn primary" disabled={sending} onClick={() => onSend?.(payload())}>
            {sending ? 'Sending…' : labels.submitSend}
          </button>
        </div>
      </div>
    </div>
  )
}

export function buildReplyDraft(email) {
  const quoted = email.body || `<p>${email.preview || ''}</p>`
  return {
    toEmail: email.email,
    subject: /^Re:/i.test(email.subject) ? email.subject : `Re: ${email.subject}`,
    bodyHtml: `<p></p><hr><p><strong>On ${email.time}, ${email.sender} &lt;${email.email}&gt; wrote:</strong></p>${quoted}`,
    accountId: email.account,
    inReplyToId: email.id,
  }
}

export function buildForwardDraft(email) {
  const quoted = email.body || `<p>${email.preview || ''}</p>`
  return {
    toEmail: '',
    subject: /^Fwd:/i.test(email.subject) ? email.subject : `Fwd: ${email.subject}`,
    bodyHtml: `<p></p><hr><p><strong>Forwarded message</strong></p><p>From: ${email.sender} &lt;${email.email}&gt;<br>Subject: ${email.subject}</p>${quoted}`,
    accountId: email.account,
    forwardedFromId: email.id,
  }
}

export function buildComposeDraft(email) {
  if (!email) return { accountId: 'gmail' }
  return {
    toEmail: email.toEmail || '',
    ccEmail: email.ccEmail || '',
    subject: email.subject || '',
    bodyHtml: email.body || '',
    accountId: email.account,
    draftId: email.id,
  }
}
